import { ClockCircleOutlined, EditOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, MomentDatePicker, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Button, Form, Popconfirm, Select, Switch, Tooltip } from 'antd'
import moment from 'moment-timezone'
import React, { useMemo } from 'react'

export function DeliveryWindow({
  form,
  selectedSite,
  selectedToDate,
  selectedFromDate,
  selectedFromTime,
  isMultipleDays,
  setIsMultipleDays,
  setUpDefaultDates,
  getNumberFromTimeString,
  setSelectedToDate,
  editingWindow,
  setEditingWindow,
  selectedRequest,
}) {
  const createTimeOptions = (
    disabledHour: number
  ): { value: number; label: string; display: Element | JSX.Element; disabled: boolean }[] => {
    return new Array(24).fill(0).map((_, i) => {
      const hour = i < 12 ? i : i - 12
      const period = i < 12 ? 'AM' : 'PM'
      return {
        value: i,
        label: `${hour || 12}:00 ${period}`,
        display: (
          <Horizontal justifyContent='space-between' verticalCenter>
            {hour || 12}:00 {period}
          </Horizontal>
        ),
        disabled: i < disabledHour,
      }
    })
  }

  const calculateEndDisabledHour = (selectedFromDateTime: moment.MomentInput) => {
    if (!selectedSite) return 0
    const fields = form.getFieldsValue()
    const currentToDate = fields.toDate || selectedToDate
    const minEndDate = moment(selectedFromDateTime)
      .tz(selectedSite.timezone)
      .add(selectedSite?.order_request_window_length, 'hours')
    if (currentToDate && currentToDate.isSame(minEndDate, 'day')) {
      return minEndDate.tz(selectedSite?.timezone).hour()
    }
    return 0
  }

  const startTimeOptions = useMemo(() => {
    if (selectedSite) {
      const isMinStartDateSelected = form
        .getFieldValue('fromDate')
        ?.isSame(selectedSite?.order_request_window_start, 'day')
      const startHour = isMinStartDateSelected
        ? moment(selectedSite.order_request_window_start).tz(selectedSite?.timezone).hour()
        : 0
      return createTimeOptions(startHour)
    }
    return createTimeOptions(0)
  }, [selectedSite, selectedFromDate])

  const endTimeOptions = useMemo(() => {
    const fromTimeNumber = getNumberFromTimeString(selectedFromTime)
    if (!selectedFromDate || !selectedToDate || (!fromTimeNumber && fromTimeNumber !== 0)) {
      return createTimeOptions(0)
    }
    const selectedFromDateTime = moment(selectedFromDate).hours(fromTimeNumber).minutes(0)
    const disabledHour = calculateEndDisabledHour(selectedFromDateTime)
    const options = createTimeOptions(disabledHour)
    return options
  }, [selectedSite, selectedFromDate, selectedFromTime, selectedToDate])

  const disabledDate = (current) => {
    if (!selectedSite || !selectedSite?.order_request_window_start) {
      // Default to disabling all dates before today if no site is selected
      return moment(current).isBefore(moment().startOf('day'))
    }

    if (selectedSite.order_request_window_start) {
      // Convert both to site timezone and compare dates only
      const windowStartDate = moment(selectedSite.order_request_window_start).tz(selectedSite.timezone).startOf('day')
      const currentDate = moment(current).startOf('day')

      // Disable dates before window start, but enable the window start date
      return currentDate.isBefore(windowStartDate)
    }
  }

  const disabledEnd = (current) => {
    const fromDate = form.getFieldValue('fromDate')
    const fromTime = form.getFieldValue('fromTime')
    if (fromDate && fromTime) {
      const fromTimeNumber = getNumberFromTimeString(selectedFromTime)
      const start = moment(fromDate).hours(fromTimeNumber).minutes(0)
      if (start) return current && current < moment(start)
    }
    return disabledDate(current)
  }
  const selectedSiteTimezone = useMemo(
    () => selectedSite?.timezone?.replace('_', ' ') ?? 'N/A',
    [selectedSite?.timezone]
  )
  const handleMultipleDayChange = (checked: boolean) => {
    if (checked) {
      const fromDate = form.getFieldValue('fromDate')
      const toDate = moment(fromDate).add(1, 'day')
      const fromTimeNumber = getNumberFromTimeString(selectedFromTime)
      form.setFieldsValue({ toDate, toTime: fromTimeNumber })
      setSelectedToDate(toDate)
      setIsMultipleDays(checked)
    } else {
      setUpDefaultDates(selectedSite)
    }
  }
  const isToggleDisabled = useMemo(() => {
    if (!selectedSite || !selectedSite?.order_request_window_start) return true
    const minStartEndAreTheSameDay = moment(selectedSite?.order_request_window_start).isSame(
      moment(selectedSite?.order_request_window_end),
      'day'
    )
    const fromTimeNumber = getNumberFromTimeString(selectedFromTime)

    const currectStartDate = moment(selectedFromDate).hours(fromTimeNumber)
    const minStartIsSelected = currectStartDate.isSame(selectedSite?.order_request_window_start, 'hour')
    if (!minStartEndAreTheSameDay && minStartIsSelected) return true
    return false
  }, [selectedSite?.order_request_window_length, selectedFromDate, selectedToDate, selectedFromTime])

  if (selectedRequest && !editingWindow) {
    const endAndStartIsSameDay = moment(selectedRequest.delivery_window_start).isSame(
      moment(selectedRequest.delivery_window_end),
      'day'
    )
    const handleWindowReset = () => {
      setUpDefaultDates(selectedSite)
      setEditingWindow(true)
    }

    return (
      <Vertical verticalCenter justifyContent='center'>
        <Horizontal alignItems='center' justifyContent='space-between'>
          <Texto align='left'>Delivery Window</Texto>
          <Texto align='left'>
            {moment(selectedRequest.delivery_window_start).format('M/D/YY h:mmA')} -{' '}
            {moment(selectedRequest.delivery_window_end).format(endAndStartIsSameDay ? 'h:mmA' : 'M/D/YY h:mmA')}
          </Texto>
        </Horizontal>
        <Horizontal className='m-0' alignItems='center' justifyContent='end'>
          <Popconfirm
            title='This will change the window to the next available.  Continue?'
            onConfirm={handleWindowReset}
          >
            <Button type='link' className='p-0'>
              <Horizontal verticalCenter>
                <Texto className='mr-2' style={{ color: 'inherit' }}>
                  EDIT WINDOW{' '}
                </Texto>
                <EditOutlined />
              </Horizontal>
            </Button>
          </Popconfirm>
        </Horizontal>
      </Vertical>
    )
  }

  return (
    <>
      <Horizontal className='pt-4 pb-1 full-height-width' alignItems='center'>
        <Vertical flex='1'>
          <Texto style={{ whiteSpace: 'nowrap' }} align='left'>
            Delivery Window
          </Texto>
        </Vertical>

        <Horizontal alignItems='center'>
          <Form.Item className='mb-0 mr-1' name='fromDate' rules={[{ required: true, message: 'From is required.' }]}>
            <MomentDatePicker disabledDate={disabledDate} placeholder='MM/DD/YY' format='MMM D, YYYY' />
          </Form.Item>
          <Form.Item className='mb-0' name='fromTime' rules={[{ required: true, message: 'From is required.' }]}>
            <Select
              options={startTimeOptions}
              placeholder='H:MM AM/PM'
              suffixIcon={<ClockCircleOutlined />}
              showSearch
              optionLabelProp='display'
            />
          </Form.Item>
        </Horizontal>

        <Texto className='mx-1'>&mdash;</Texto>

        <Horizontal alignItems='center'>
          <Form.Item
            hidden={!isMultipleDays}
            className='mb-0 mr-1'
            name='toDate'
            rules={[{ required: true, message: 'To is required.' }]}
          >
            <MomentDatePicker placeholder='MM/DD/YY' format='MMM D, YYYY' disabledDate={disabledEnd} />
          </Form.Item>

          <Form.Item className='mb-0' name='toTime' rules={[{ required: true, message: 'To is required.' }]}>
            <Select
              options={endTimeOptions}
              placeholder='H:MM AM/PM'
              suffixIcon={<ClockCircleOutlined />}
              showSearch
              optionLabelProp='display'
            />
          </Form.Item>
        </Horizontal>
      </Horizontal>
      <Vertical verticalCenter alignItems='flex-end' className='p-1'>
        <Horizontal verticalCenter>
          <Texto className='mr-2'>Multi-Day Window</Texto>
          <Tooltip
            placement='topRight'
            title={!selectedSite ? 'Please select a site.' : isToggleDisabled && 'Default window spans multiple days.'}
          >
            <Switch
              size='small'
              checked={isMultipleDays}
              onChange={handleMultipleDayChange}
              disabled={isToggleDisabled}
            />
          </Tooltip>
        </Horizontal>
        <Texto align='right' className='p-1'>
          Delivery Window uses Site's Timezone ({selectedSiteTimezone})
        </Texto>
      </Vertical>
    </>
  )
}
